import React, { createContext, useContext, useRef, useState, useCallback } from "react";
import ConfirmModal from "../components/Common/ConfirmModal";

const ConfirmContext = createContext(null);

const defaultOptions = {
  title: "Confirm",
  message: "Are you sure?",
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
  variant: "primary",
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({
    isOpen: false,
    ...defaultOptions,
  });
  const resolveRef = useRef(null);

  const showConfirm = useCallback((options = {}) => {
    const opts = { ...defaultOptions, ...options };
    setState({
      isOpen: true,
      title: opts.title,
      message: opts.message,
      confirmLabel: opts.confirmLabel ?? defaultOptions.confirmLabel,
      cancelLabel: opts.cancelLabel ?? defaultOptions.cancelLabel,
      variant: opts.variant ?? defaultOptions.variant,
    });
    return new Promise((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (resolveRef.current) {
      resolveRef.current(true);
      resolveRef.current = null;
    }
    setState((s) => ({ ...s, isOpen: false }));
  }, []);

  const handleCancel = useCallback(() => {
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }
    setState((s) => ({ ...s, isOpen: false }));
  }, []);

  return (
    <ConfirmContext.Provider value={{ showConfirm }}>
      {children}
      <ConfirmModal
        isOpen={state.isOpen}
        toggle={handleCancel}
        title={state.title}
        message={state.message}
        confirmLabel={state.confirmLabel}
        cancelLabel={state.cancelLabel}
        variant={state.variant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used within ConfirmProvider");
  }
  return ctx;
}
